
import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";

interface GoogleMapProps {
  location: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  zoom?: number;
  height?: string;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ 
  location,
  latitude,
  longitude,
  placeId,
  zoom = 15, 
  height = '300px' 
}) => {
  const [mapUrls, setMapUrls] = useState<{ embedUrl: string; directUrl: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchMapUrls = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase.functions.invoke('map-embed', {
          body: { 
            location, 
            latitude, 
            longitude,
            placeId,
            zoom 
          }
        });
        
        if (error) {
          console.error('Error fetching map URLs:', error);
          setError('Failed to load map. Please try again later.');
        } else {
          setMapUrls(data);
        }
      } catch (err) {
        console.error('Error invoking map-embed function:', err);
        setError('Failed to load map. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMapUrls();
  }, [location, latitude, longitude, placeId, zoom]);

  if (isLoading) {
    return (
      <div className="relative rounded-md overflow-hidden border border-border bg-muted h-[300px] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (error || !mapUrls) {
    return (
      <div className="relative rounded-md overflow-hidden border border-border bg-muted h-[300px] flex items-center justify-center">
        <div className="text-center p-4">
          <p className="text-muted-foreground">{error || 'Failed to load map.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-md overflow-hidden border border-border">
      <iframe
        title="Google Map"
        width="100%"
        height={height}
        style={{ border: 0 }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        src={mapUrls.embedUrl}
      ></iframe>
      <div className="absolute bottom-2 right-2">
        <a 
          href={mapUrls.directUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 bg-white/90 text-black text-xs px-2 py-1 rounded-md hover:bg-white"
        >
          <ExternalLink className="h-3 w-3" />
          View Larger Map
        </a>
      </div>
    </div>
  );
};

export default GoogleMap;
